import { DriverStatus } from '@repo/shared'

import type {
  EcoScore,
  Message,
  TachographEntry,
  Task,
  Vehicle,
} from './types/types'

export const mockVehicles: Array<
  Vehicle & {
    telematics?: {
      fuelFlowRate: number // L/h
      engineRpm: number
      operationalHours: number
      engineTemp: number // °C
      waterTemp: number // °C
      currentWeight: number // kg
      fuelTank1: number // %
      fuelTank2: number // %
    }
    specifications?: {
      maxLoadCapacity: number // kg
      length: number // m
      width: number // m
      height: number // m
      engineType: string
      emissionStandard: string
    }
    tirePressure?: {
      frontLeft: number
      frontRight: number
      rearLeft: number
      rearRight: number
      lastChecked: string
    }
    documents?: Array<{
      id: string
      name: string
      type: string
      issueDate: string
      expiryDate: string
      status: string
    }>
    trailer?: {
      chassisNumber: string
      licensePlate: string
      dimensions: string
      type: string
    }
  }
> = [
  {
    id: 'v1',
    licensePlate: 'ABC-1234',
    vehicleModel: 'Volvo FH16',
    type: 'truck',
    status: 'active',
    driverId: 'd1',
    location: { lat: 52.52, lng: 13.405, address: 'Berlin, Germany' },
    fuel: 75,
    mileage: 145230,
    lastUpdate: new Date().toISOString(),
    trailer: {
      id: 't1',
      chassisNumber: 'WDB9634321L789012',
      licensePlate: 'TR-5678',
      dimensions: '13.6m x 2.45m x 2.7m',
      type: 'tarpaulin',
    },
    tirePressure: {
      frontLeft: 9.0,
      frontRight: 9.0,
      rearLeft: 8.5,
      rearRight: 8.5,
      lastChecked: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    documents: [
      {
        id: 'doc1-1',
        name: 'Vehicle Registration',
        type: 'registration',
        issueDate: '2023-01-15',
        expiryDate: '2025-01-15',
        status: 'valid',
      },
      {
        id: 'doc1-2',
        name: 'Insurance Certificate',
        type: 'insurance',
        issueDate: '2024-06-01',
        expiryDate: '2025-06-01',
        status: 'valid',
      },
      {
        id: 'doc1-3',
        name: 'Technical Inspection',
        type: 'inspection',
        issueDate: '2024-09-10',
        expiryDate: '2025-09-10',
        status: 'valid',
      },
      {
        id: 'doc1-4',
        name: 'Transport License',
        type: 'license',
        issueDate: '2023-03-20',
        expiryDate: '2025-03-20',
        status: 'valid',
      },
    ],
    telematics: {
      fuelFlowRate: 28.5,
      engineRpm: 1450,
      operationalHours: 12450,
      engineTemp: 87,
      waterTemp: 82,
      currentWeight: 18500,
      fuelTank1: 75,
      fuelTank2: 72,
    },
    specifications: {
      maxLoadCapacity: 24000,
      length: 16.5,
      width: 2.55,
      height: 4.0,
      engineType: 'D13K540 Euro 6',
      emissionStandard: 'Euro 6',
    },
  },
  {
    id: 'v2',
    licensePlate: 'XYZ-5678',
    vehicleModel: 'Mercedes Actros',
    type: 'truck',
    status: 'active',
    location: { lat: 48.8566, lng: 2.3522, address: 'Paris, France' },
    fuel: 45,
    mileage: 98450,
    lastUpdate: new Date().toISOString(),
    tirePressure: {
      frontLeft: 8.8,
      frontRight: 9.0,
      rearLeft: 8.3,
      rearRight: 8.4,
      lastChecked: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    documents: [
      {
        id: 'doc2-1',
        name: 'Vehicle Registration',
        type: 'registration',
        issueDate: '2022-11-20',
        expiryDate: '2024-11-20',
        status: 'expired',
      },
      {
        id: 'doc2-2',
        name: 'Insurance Certificate',
        type: 'insurance',
        issueDate: '2024-05-15',
        expiryDate: '2025-05-15',
        status: 'valid',
      },
      {
        id: 'doc2-3',
        name: 'Technical Inspection',
        type: 'inspection',
        issueDate: '2024-08-01',
        expiryDate: '2025-02-01',
        status: 'expired',
      },
    ],
    telematics: {
      fuelFlowRate: 0,
      engineRpm: 0,
      operationalHours: 8920,
      engineTemp: 35,
      waterTemp: 32,
      currentWeight: 12000,
      fuelTank1: 45,
      fuelTank2: 48,
    },
    specifications: {
      maxLoadCapacity: 26000,
      length: 18.75,
      width: 2.55,
      height: 4.0,
      engineType: 'OM 471 Euro 6',
      emissionStandard: 'Euro 6',
    },
  },
  {
    id: 'v3',
    licensePlate: 'DEF-9012',
    vehicleModel: 'Scania R450',
    type: 'truck',
    status: 'active',
    driverId: 'd2',
    location: { lat: 51.5074, lng: -0.1278, address: 'London, UK' },
    fuel: 90,
    mileage: 67890,
    lastUpdate: new Date().toISOString(),
    tirePressure: {
      frontLeft: 9.2,
      frontRight: 9.1,
      rearLeft: 8.7,
      rearRight: 8.8,
      lastChecked: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    },
    documents: [
      {
        id: 'doc3-1',
        name: 'Vehicle Registration',
        type: 'registration',
        issueDate: '2023-07-10',
        expiryDate: '2025-07-10',
        status: 'valid',
      },
      {
        id: 'doc3-2',
        name: 'Insurance Certificate',
        type: 'insurance',
        issueDate: '2024-04-01',
        expiryDate: '2025-04-01',
        status: 'valid',
      },
      {
        id: 'doc3-3',
        name: 'Technical Inspection',
        type: 'inspection',
        issueDate: '2024-10-15',
        expiryDate: '2025-10-15',
        status: 'valid',
      },
      {
        id: 'doc3-4',
        name: 'Transport License',
        type: 'license',
        issueDate: '2023-02-28',
        expiryDate: '2025-01-15',
        status: 'valid',
      },
    ],
    telematics: {
      fuelFlowRate: 32.1,
      engineRpm: 1580,
      operationalHours: 6234,
      engineTemp: 91,
      waterTemp: 85,
      currentWeight: 21500,
      fuelTank1: 90,
      fuelTank2: 88,
    },
    specifications: {
      maxLoadCapacity: 25000,
      length: 17.2,
      width: 2.55,
      height: 4.0,
      engineType: 'DC13 165 Euro 6',
      emissionStandard: 'Euro 6',
    },
  },
]

export const mockDrivers = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@fleet.com',
    phone: '+49 123 456 789',
    licenseNumber: 'DL123456',
    status: DriverStatus.Driving,
    vehicleId: 'v1',
    hoursWorked: 6.5,
    hoursRemaining: 2.5,
  },
  {
    id: 2,
    email: 'maria.garcia@fleet.com',
    phone: '+44 987 654 321',
    licenseNumber: 'DL789012',
    status: DriverStatus.Driving,
    vehicleId: 'v3',
    hoursWorked: 4.0,
    hoursRemaining: 5.0,
  },
  {
    id: 3,
    name: 'Hans Mueller',
    email: 'hans.mueller@fleet.com',
    phone: '+49 555 123 456',
    licenseNumber: 'DL345678',
    status: DriverStatus.Resting,
    vehicleId: null,
    hoursWorked: 9.0,
    hoursRemaining: 0,
  },
]

export const mockTachographData: Array<TachographEntry> = [
  // Driver 1 (John Smith) - Current day activities with extended data
  {
    id: 't1-1',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(6, 0, 0, 0)).toISOString(),
    duration: 360,
    location: 'Edisonstraße, 85098 Großmehring, Njemačka',
    odometer: 57629.5,
    speed: 0,
    fuelLevel: 91,
    distance: 0,
    eventType: 'first-position',
  },
  {
    id: 't1-2',
    driverId: 'd1',
    activity: 'available',
    startTime: new Date(new Date().setHours(6, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(6, 30, 0, 0)).toISOString(),
    duration: 30,
    location: 'Edisonstraße, 85098 Großmehring, Njemačka',
    odometer: 57629.5,
    speed: 0,
    fuelLevel: 91,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't1-3',
    driverId: 'd1',
    activity: 'work',
    startTime: new Date(new Date().setHours(6, 30, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(6, 34, 0, 0)).toISOString(),
    duration: 4,
    location: 'Edisonstraße, 85098 Großmehring, Njemačka',
    odometer: 57629.5,
    speed: 18,
    fuelLevel: 91,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't1-4',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(new Date().setHours(6, 34, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(9, 1, 0, 0)).toISOString(),
    duration: 147,
    location: 'Burgebrach, Bayern, Njemačka',
    odometer: 57792.7,
    speed: 0,
    fuelLevel: 80,
    distance: 163.2,
    eventType: 'stopped',
  },
  {
    id: 't1-5',
    driverId: 'd1',
    activity: 'work',
    startTime: new Date(new Date().setHours(9, 1, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(11, 44, 0, 0)).toISOString(),
    duration: 163,
    location: 'Burgebrach, Bayern, Njemačka',
    odometer: 57792.7,
    speed: 2,
    fuelLevel: 80,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't1-6',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(new Date().setHours(11, 44, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(11, 49, 0, 0)).toISOString(),
    duration: 5,
    location: 'St2262, 96138 Burgebrach, Njemačka',
    odometer: 57792.9,
    speed: 1,
    fuelLevel: 80,
    distance: 0.2,
    eventType: 'stopped',
  },
  {
    id: 't1-7',
    driverId: 'd1',
    activity: 'work',
    startTime: new Date(new Date().setHours(11, 49, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(11, 52, 0, 0)).toISOString(),
    duration: 3,
    location: 'St2262, 96138 Burgebrach, Njemačka',
    odometer: 57792.9,
    speed: 2,
    fuelLevel: 80,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't1-8',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(new Date().setHours(11, 52, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(11, 56, 0, 0)).toISOString(),
    duration: 4,
    location: 'St2262, 96138 Burgebrach, Njemačka',
    odometer: 57793.0,
    speed: 0,
    fuelLevel: 80,
    distance: 0,
    eventType: 'stopped',
  },

  // Driver 2 (Maria Garcia) - Current day activities with extended data
  {
    id: 't2-1',
    driverId: 'd2',
    activity: 'rest',
    startTime: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(7, 0, 0, 0)).toISOString(),
    duration: 420,
    location: 'London Hotel',
    odometer: 45230.0,
    speed: 0,
    fuelLevel: 85,
    distance: 0,
    eventType: 'first-position',
  },
  {
    id: 't2-2',
    driverId: 'd2',
    activity: 'available',
    startTime: new Date(new Date().setHours(7, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(7, 45, 0, 0)).toISOString(),
    duration: 45,
    location: 'London Depot',
    odometer: 45230.0,
    speed: 0,
    fuelLevel: 85,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't2-3',
    driverId: 'd2',
    activity: 'work',
    startTime: new Date(new Date().setHours(7, 45, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(8, 15, 0, 0)).toISOString(),
    duration: 30,
    location: 'London Depot - Pre-trip Check',
    odometer: 45230.0,
    speed: 0,
    fuelLevel: 85,
    distance: 0,
    eventType: 'started',
  },
  {
    id: 't2-4',
    driverId: 'd2',
    activity: 'driving',
    startTime: new Date(new Date().setHours(8, 15, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(11, 30, 0, 0)).toISOString(),
    duration: 195,
    location: 'London to Birmingham',
    odometer: 45380.5,
    speed: 85,
    fuelLevel: 72,
    distance: 150.5,
    eventType: 'started',
  },
  {
    id: 't2-5',
    driverId: 'd2',
    activity: 'work',
    startTime: new Date(new Date().setHours(11, 30, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(12, 15, 0, 0)).toISOString(),
    duration: 45,
    location: 'Birmingham - Unloading',
    odometer: 45380.5,
    speed: 0,
    fuelLevel: 72,
    distance: 0,
    eventType: 'stopped',
  },
  {
    id: 't2-6',
    driverId: 'd2',
    activity: 'rest',
    startTime: new Date(new Date().setHours(12, 15, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(13, 0, 0, 0)).toISOString(),
    duration: 45,
    location: 'Birmingham Rest Area',
    odometer: 45380.5,
    speed: 0,
    fuelLevel: 72,
    distance: 0,
    eventType: 'stopped',
  },
  {
    id: 't2-7',
    driverId: 'd2',
    activity: 'driving',
    startTime: new Date(new Date().setHours(13, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(15, 30, 0, 0)).toISOString(),
    duration: 150,
    location: 'Birmingham to Manchester',
    odometer: 45510.8,
    speed: 90,
    fuelLevel: 65,
    distance: 130.3,
    eventType: 'started',
  },
  {
    id: 't2-8',
    driverId: 'd2',
    activity: 'available',
    startTime: new Date(new Date().setHours(15, 30, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(16, 0, 0, 0)).toISOString(),
    duration: 30,
    location: 'Manchester Depot',
    odometer: 45510.8,
    speed: 0,
    fuelLevel: 65,
    distance: 0,
    eventType: 'stopped',
  },

  // Driver 3 (Hans Mueller) - Current day activities with extended data
  {
    id: 't3-1',
    driverId: 'd3',
    activity: 'driving',
    startTime: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(2, 30, 0, 0)).toISOString(),
    duration: 150,
    location: 'Munich to Stuttgart',
    odometer: 67890.0,
    speed: 95,
    fuelLevel: 88,
    distance: 220.5,
    eventType: 'first-position',
  },
  {
    id: 't3-2',
    driverId: 'd3',
    activity: 'rest',
    startTime: new Date(new Date().setHours(2, 30, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(3, 15, 0, 0)).toISOString(),
    duration: 45,
    location: 'Stuttgart Rest Stop',
    odometer: 68110.5,
    speed: 0,
    fuelLevel: 82,
    distance: 0,
    eventType: 'stopped',
  },
  {
    id: 't3-3',
    driverId: 'd3',
    activity: 'driving',
    startTime: new Date(new Date().setHours(3, 15, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(7, 0, 0, 0)).toISOString(),
    duration: 225,
    location: 'Stuttgart to Frankfurt',
    odometer: 68295.8,
    speed: 88,
    fuelLevel: 70,
    distance: 185.3,
    eventType: 'started',
  },
  {
    id: 't3-4',
    driverId: 'd3',
    activity: 'work',
    startTime: new Date(new Date().setHours(7, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(8, 0, 0, 0)).toISOString(),
    duration: 60,
    location: 'Frankfurt - Delivery',
    odometer: 68295.8,
    speed: 0,
    fuelLevel: 70,
    distance: 0,
    eventType: 'stopped',
  },
  {
    id: 't3-5',
    driverId: 'd3',
    activity: 'rest',
    startTime: new Date(new Date().setHours(8, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(19, 0, 0, 0)).toISOString(),
    duration: 660,
    location: 'Frankfurt Hotel - Mandatory Rest',
    odometer: 68295.8,
    speed: 0,
    fuelLevel: 70,
    distance: 0,
    eventType: 'rest-period',
  },
  {
    id: 't3-6',
    driverId: 'd3',
    activity: 'available',
    startTime: new Date(new Date().setHours(19, 0, 0, 0)).toISOString(),
    endTime: new Date(new Date().setHours(20, 0, 0, 0)).toISOString(),
    duration: 60,
    location: 'Frankfurt Hotel',
    odometer: 68295.8,
    speed: 0,
    fuelLevel: 70,
    distance: 0,
    eventType: 'started',
  },

  // Previous day data for Driver 1 (for historical view)
  {
    id: 't1-prev-1',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(
      new Date().setDate(new Date().getDate() - 1),
    ).toISOString(),
    endTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        6,
        0,
        0,
        0,
      ),
    ).toISOString(),
    duration: 360,
    location: 'Berlin Rest Area',
  },
  {
    id: 't1-prev-2',
    driverId: 'd1',
    activity: 'driving',
    startTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        6,
        0,
        0,
        0,
      ),
    ).toISOString(),
    endTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        10,
        30,
        0,
        0,
      ),
    ).toISOString(),
    duration: 270,
    location: 'Berlin to Leipzig',
  },
  {
    id: 't1-prev-3',
    driverId: 'd1',
    activity: 'rest',
    startTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        10,
        30,
        0,
        0,
      ),
    ).toISOString(),
    endTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        11,
        15,
        0,
        0,
      ),
    ).toISOString(),
    duration: 45,
    location: 'Leipzig Rest Stop',
  },
  {
    id: 't1-prev-4',
    driverId: 'd1',
    activity: 'driving',
    startTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        11,
        15,
        0,
        0,
      ),
    ).toISOString(),
    endTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        15,
        45,
        0,
        0,
      ),
    ).toISOString(),
    duration: 270,
    location: 'Leipzig to Dresden',
  },
  {
    id: 't1-prev-5',
    driverId: 'd1',
    activity: 'available',
    startTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        15,
        45,
        0,
        0,
      ),
    ).toISOString(),
    endTime: new Date(
      new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
        16,
        0,
        0,
        0,
      ),
    ).toISOString(),
    duration: 15,
    location: 'Dresden Depot',
  },
]

export const mockTasks: Array<Task> = [
  {
    id: 'task1',
    title: 'Vehicle Inspection',
    description: 'Complete routine inspection for ABC-1234',
    priority: 'high',
    status: 'pending',
    assignedTo: 'd1',
    vehicleId: 'v1',
    dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'task2',
    title: 'Delivery Confirmation',
    description: 'Confirm delivery at Paris warehouse',
    priority: 'urgent',
    status: 'in-progress',
    assignedTo: 'd1',
    vehicleId: 'v1',
    dueDate: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    location: 'Paris, France',
  },
]

export const mockMessages: Array<Message> = [
  // Conversation with John Smith (d1)
  {
    id: 'msg1',
    senderId: 'admin',
    receiverId: 'd1',
    content: "Hi John, how's the delivery going?",
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg2',
    senderId: 'd1',
    receiverId: 'admin',
    content:
      'Going well! Just passed Frankfurt, should arrive in Paris on schedule.',
    timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg3',
    senderId: 'admin',
    receiverId: 'd1',
    content: "Great! Don't forget to complete the vehicle inspection report.",
    timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg4',
    senderId: 'd1',
    receiverId: 'admin',
    content: "Will do. I'll send it after the delivery.",
    timestamp: new Date(Date.now() - 45 * 60 * 60 * 1000).toISOString(),
    read: false,
  },

  // Conversation with Maria Garcia (d2)
  {
    id: 'msg5',
    senderId: 'admin',
    receiverId: 'd2',
    content: 'Maria, can you pick up an extra load in Manchester?',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg6',
    senderId: 'd2',
    receiverId: 'admin',
    content: "Sure, I have time. What's the destination?",
    timestamp: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg7',
    senderId: 'admin',
    receiverId: 'd2',
    content: 'Liverpool. Should be ready by 5 PM.',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg8',
    senderId: 'd2',
    receiverId: 'admin',
    content: "Perfect, I'll head there after unloading in Manchester.",
    timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg9',
    senderId: 'd2',
    receiverId: 'admin',
    content: 'Just arrived in Manchester. Unloading now.',
    timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
    read: false,
  },

  // Conversation with Hans Mueller (d3)
  {
    id: 'msg10',
    senderId: 'admin',
    receiverId: 'd3',
    content:
      "Hans, you've reached your driving hours limit. Please take your mandatory rest.",
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg11',
    senderId: 'd3',
    receiverId: 'admin',
    content: "Understood. I'm at the Frankfurt hotel now, resting.",
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    id: 'msg12',
    senderId: 'admin',
    receiverId: 'd3',
    content: "Good. Let me know when you're ready for the next assignment.",
    timestamp: new Date(Date.now() - 3.5 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
]

export const mockEcoScores: Array<EcoScore> = [
  {
    driverId: 'd1',
    driverName: 'John Smith',
    overallScore: 87,
    suddenBraking: 12,
    suddenAcceleration: 8,
    idleTime: 45,
    suddenTurns: 5,
    distanceTraveled: 1250.5,
    drivingTime: 42.5,
    avgFuelConsumption: 28.3,
    ecoSpeed: 78,
    period: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
    },
  },
  {
    driverId: 'd2',
    driverName: 'Maria Garcia',
    overallScore: 92,
    suddenBraking: 6,
    suddenAcceleration: 4,
    idleTime: 28,
    suddenTurns: 3,
    distanceTraveled: 1580.2,
    drivingTime: 48.0,
    avgFuelConsumption: 26.8,
    ecoSpeed: 85,
    period: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
    },
  },
  {
    driverId: 'd3',
    driverName: 'Hans Mueller',
    overallScore: 74,
    suddenBraking: 18,
    suddenAcceleration: 15,
    idleTime: 62,
    suddenTurns: 9,
    distanceTraveled: 980.7,
    drivingTime: 38.5,
    avgFuelConsumption: 31.2,
    ecoSpeed: 65,
    period: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
    },
  },
]
